import React, { useState, useEffect, useCallback } from "react";
import { BrokerLeadGenerationTool } from 'broker-lead-generation-tool';
import { Spinner } from "react-bootstrap";
import './BrokerContactComponent.css'; // We'll create this CSS file next

// This is a custom wrapper for the BrokerLeadGenerationTool that adds icons and custom styling
const BrokerContactComponent = ({ brokerData }) => {
  // State variables for form data
  const [formData, setFormData] = useState({
    companyName: "",
    loanOfficerUid: "",
    leadSource: "SELFGEN", // Default value from original code
    loanOfficerEmail: "",
    webPageURL: "",
    brokerClient: true,
    termsAndConditionsURL: "",
    privacyPolicyURL: "",
    applyNowStateLicenseIn: ""
  });
  
  const [isLoaded, setIsLoaded] = useState(false);
  const formRef = useCallback(node => {
    // This can be used to access the form element directly if needed
    if (node) {
      // Once the component is mounted, apply our custom styling
      applyCustomStyling(node);
    }
  }, []);

  // Function to apply custom styling to the form inputs
  const applyCustomStyling = (formNode) => {
    if (!formNode) return;
    
    // setTimeout to ensure the DOM is fully rendered
    setTimeout(() => {
      // Get all input fields within the form
      const inputFields = formNode.querySelectorAll('input');
      
      // Define icons for specific placeholders
      const placeholderIconMap = {
        'First Name': 'user_icon.webp',
        'Last Name': 'user_icon.webp',
        'Email Address': 'email_icon.webp',
        'Telephone Number': 'phone_icon.webp',
        'Your Property State': 'location_icon.webp',
        'Tell us about your needs and special circumstances': 'comment_icon.webp'
      };
      
      // Fallback icons by input type
      const typeIconMap = {
        'text': 'user_icon.webp',
        'email': 'email_icon.webp',
        'tel': 'phone_icon.webp',
        'textarea': 'comment_icon.webp',
        'select': 'location_icon.webp',
        'default': 'form.webp'
      };
      
      // Process each input field
      inputFields.forEach(input => {
        const inputWrapper = input.parentElement;
        
        // Skip if already processed
        if (inputWrapper.classList.contains('custom-input-wrapper')) return;
        
        // Create a new wrapper div
        const customWrapper = document.createElement('div');
        customWrapper.className = 'custom-input-wrapper';
        
        // Determine the appropriate icon
        let iconName = typeIconMap.default;
        
        // Check if there's a specific icon for this placeholder
        if (input.placeholder && placeholderIconMap[input.placeholder]) {
          iconName = placeholderIconMap[input.placeholder];
        }
        // Otherwise try to determine by input type
        else if (typeIconMap[input.type]) {
          iconName = typeIconMap[input.type];
        }
        // For textareas which might not have a placeholder set
        else if (input.tagName.toLowerCase() === 'textarea') {
          iconName = typeIconMap.textarea;
        }
        // For select elements
        else if (input.tagName.toLowerCase() === 'select') {
          iconName = typeIconMap.select;
        }
        
        // Create icon element
        const iconElement = document.createElement('img');
        iconElement.src = `https://d2b7dijo04ypct.cloudfront.net/brokers-landingpages-resources/images/${iconName}`;
        iconElement.className = 'input-icon';
        iconElement.alt = 'input icon';
        
        // Add the icon to the wrapper
        customWrapper.appendChild(iconElement);
        
        // Replace the input in the DOM with our custom wrapper
        inputWrapper.insertBefore(customWrapper, input);
        customWrapper.appendChild(input);
        
        // Add custom class to the input for styling
        input.classList.add('custom-styled-input');
      });
    }, 500); // Give time for the form to fully render
  };

  useEffect(() => {
    // Make sure we have the broker data
    if (brokerData && brokerData.companyCode) {
      // Fetch client specific terms and policy details
      fetch(`${process.env.REACT_APP_CDN}brokers-landingpages-resources/clientSpecificTermsPolicyDetails/${brokerData.companyCode}.json`)
        .then(response => response.json())
        .then(clientSpecificTermsPolicyData => {
          const clientSpecificData = clientSpecificTermsPolicyData[0];
          
          // Set all the form data at once
          setFormData({
            companyName: brokerData.companyName,
            loanOfficerUid: brokerData.loID,
            leadSource: "SELFGEN",
            loanOfficerEmail: brokerData.email,
            webPageURL: brokerData.profileUrl,
            brokerClient: clientSpecificData.brokerClient,
            termsAndConditionsURL: clientSpecificData.termsAndConditionsURL,
            privacyPolicyURL: clientSpecificData.privacyPolicyURL,
            applyNowStateLicenseIn: brokerData.applyNowStateLicenseIn
          });
          
          setIsLoaded(true);
        })
        .catch(error => {
          console.error("Error fetching client specific data:", error);
        });
    }
  }, [brokerData]);

  // Inject our custom CSS styles via effect
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Custom styles for the input fields */
      .custom-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
      }
      
      .input-icon {
        position: absolute;
        left: 10px;
        width: 20px;
        height: 20px;
        z-index: 2;
      }
      
      .custom-styled-input {
        padding-left: 40px !important;
        border: none !important;
        border-bottom: 2px solid rgba(255, 86, 1, 0.50) !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        transition: border-color 0.3s ease !important;
      }
      
      .custom-styled-input:focus {
        border-bottom: 2px solid rgba(255, 86, 1, 1) !important;
        outline: none !important;
      }
      
      /* Apply styles to any select elements inside the broker lead form */
      .broker-contact-component select {
        border: none !important;
        border-bottom: 2px solid rgba(255, 86, 1, 0.50) !important;
        border-radius: 0 !important;
        box-shadow: none !important;
      }
      
      /* Apply styles to any textarea elements inside the broker lead form */
      .broker-contact-component textarea {
        border: none !important;
        border-bottom: 2px solid rgba(255, 86, 1, 0.50) !important;
        border-radius: 0 !important;
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(styleElement);
    
    // Cleanup function
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <div id="broker-contact-component">
      {isLoaded && formData.loanOfficerUid ? (
        <BrokerLeadGenerationTool
          companyName={formData.companyName}
          leadSource={formData.leadSource}
          loanOfficerUid={formData.loanOfficerUid}
          toEmailAddress={formData.loanOfficerEmail}
          statesDomain="ny.swmc.com"
          webPageURL={formData.webPageURL}
          brokerClient={formData.brokerClient}
          termsAndConditionsURL={formData.termsAndConditionsURL}
          privacyPolicyURL={formData.privacyPolicyURL}
          applyNowStateLicenseIn={formData.applyNowStateLicenseIn}
          ref={formRef}
        />
      ) : (
        <div className="loading-container">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default BrokerContactComponent;